const post = {
  slug: "discount-codes-and-free-shipping-for-instagram-orders",
  title: "Discount Codes and Free Shipping for Instagram Orders: How They Work",
  seoTitle: "Discount Codes and Free Shipping on Instagram",
  description:
    "How shipping fees, free-shipping thresholds, discount codes and automatic offers work in Checkout DM, including which offer wins when more than one applies.",
  excerpt:
    "Shipping fees, free shipping above a cart value, manual codes and automatic offers: how each one works in Checkout DM and how the system decides which discount a customer gets.",
  category: "Automation",
  tags: ["Instagram discount codes", "Free shipping Instagram", "Coupons for Instagram sellers", "Checkout DM offers"],
  date: "2026-11-10",
  cover: {
    src: "/blog-images/discount-codes-and-free-shipping-for-instagram-orders-cover.png",
    alt: "A phone showing a discount code and a free shipping message in an Instagram DM checkout",
  },
  blocks: [
    {
      type: "p",
      text:
        "Offers decide whether a hesitant buyer pays or walks away. But when you sell through DMs, every discount you grant by hand costs you a message, a calculation and the risk of getting the number wrong. Checkout DM lets you set the rules once, and the chat applies them at checkout.",
    },
    {
      type: "p",
      text:
        "This guide explains how the pieces fit together: the shipping fee, free shipping above a cart value, manual discount codes, automatic offers and the rules that decide which one a customer gets. Each feature is described as it works today, including which plan it needs.",
    },

    { type: "h2", text: "Start with your shipping fee" },
    {
      type: "p",
      text:
        "A shipping fee is a flat delivery charge added to every order\u2019s total. It is available on every plan, and it is passed on to the customer, not deducted from what you earn. Set it to 0 if you want free shipping on everything.",
    },
    {
      type: "p",
      text:
        "For example, with a fee of \u20B949, a customer\u2019s \u20B9450 cart becomes \u20B9499 at checkout, before any discount is applied.",
    },

    { type: "h2", text: "Free shipping above a cart value" },
    {
      type: "p",
      text:
        "On Starter, Growth and Pro you can waive the shipping fee automatically once a customer\u2019s cart crosses a value you choose. It applies to every order that qualifies, with no limit on how often. The idea is simple: instead of paying for delivery, a customer adds one more item to reach the threshold. You can leave the field blank to turn it off.",
    },
    {
      type: "p",
      text:
        "Choose the threshold with your own numbers in mind. If your average order is \u20B9700, a threshold slightly above that gives people a reason to add something small. Our [simple margin guide for pricing on Instagram](/blog/price-products-on-instagram-india) helps you check that the free delivery still leaves you a profit.",
    },

    { type: "h2", text: "Manual codes and automatic offers" },
    {
      type: "p",
      text:
        "Discount rules are available on Growth and Pro. There are two ways a rule can reach a customer:",
    },
    {
      type: "ul",
      items: [
        "**A manual code** is something the customer types in the DM, such as WELCOME10.",
        "**An automatic offer** applies on its own the moment a cart qualifies, with no code needed. For example, any cart above \u20B9500 could get the offer without the customer doing anything.",
      ],
    },
    {
      type: "p",
      text:
        "The same rule can be set up either way. If an automatic offer already covers the cart, the chat does not ask for a code. It prompts for a manual code only when no automatic offer applies and you have at least one active manual code.",
    },

    { type: "h2", text: "Offers that end on their own" },
    {
      type: "ul",
      items: [
        "**Time-based offers** have a start and an end time. The times you enter use your account\u2019s timezone. Once the end time passes, the rule stops applying, even for customers who never used it.",
        "**Count-based offers** expire automatically after a set number of uses.",
        "**Once per customer.** After a customer redeems a rule, automatic or manual, it will not apply to their next order. You do not need to babysit codes to stop repeat use.",
      ],
    },
    {
      type: "p",
      text:
        "Growth and Pro can also show active coupons on the public catalog page, and you can feature up to three coupons at a time.",
    },

    {
      type: "figure",
      src: "/blog-images/discount-codes-and-free-shipping-for-instagram-orders-figure.png",
      alt: "How Checkout DM decides which discount applies: automatic offers first, the best saving wins, a manual code is asked for only if none applies, one discount per order",
      caption: "How the discount for an order is decided.",
      width: 1200,
      height: 1035,
    },

    { type: "h2", text: "Which discount does the customer get?" },
    {
      type: "p",
      text:
        "This is the part that surprises sellers, so it is worth knowing:",
    },
    {
      type: "ul",
      items: [
        "**One discount per order.** A single order can never combine two rules.",
        "**Automatic offers win over manual codes.**",
        "**The biggest saving wins.** If more than one automatic offer qualifies, the customer automatically gets whichever one saves them the most money, not whichever you created first.",
        "**Avoid identical offers.** If two active automatic offers would save exactly the same amount, only one applies, and which one is not guaranteed to be consistent.",
      ],
    },
    {
      type: "p",
      text:
        "A quick example: a \u20B9700 cart that qualifies for both a flat \u20B9100 off and 15% off would save \u20B9100 with one and \u20B9105 with the other, so the customer gets the 15% off.",
    },

    { type: "h2", text: "Quantity-based offers on Pro" },
    {
      type: "p",
      text:
        "Pro adds quantity-based offers, such as Buy X, Get Y% Off. An automatic offer can require a minimum cart quantity in addition to a minimum order value. When both are set, the cart has to clear both conditions together. If a rule gives \u20B9100 off orders over \u20B91,000 with a minimum quantity of 2, then a cart of three items worth \u20B9600 does not qualify, and neither does a single \u20B91,200 item.",
    },

    { type: "h2", text: "Using offers to answer \u201Ccan I get a discount?\u201D" },
    {
      type: "p",
      text:
        "Buyers often ask for a discount just before they pay. On Growth and Pro, if a customer tells the chat why they hesitated, for example by asking for a discount, that reason is shown to you in your Stuck tab. Combined with automatic recovery messages, as described in our guide to [abandoned carts in Instagram DMs](/blog/abandoned-carts-instagram-dms), you can see what holds buyers back and decide whether an offer is the right answer.",
    },
    {
      type: "callout",
      tone: "peach",
      title: "Offers are your call",
      text:
        "Checkout DM applies the rules you set, and only the account owner can edit them. A discount that looks generous today can quietly cut your margin, so check the numbers before you switch an offer on.",
    },
  ],
  related: [
    { to: "/discounts-guide", title: "Shipping Fees & Discounts Guide", description: "The full rules for shipping fees and discount priority." },
    { to: "/pricing", title: "Pricing", description: "Which plan includes shipping rules, coupons and offers." },
    { to: "/abandoned-cart-guide", title: "Abandoned Cart Guide", description: "How cart reminders and recovery work." },
  ],
  faqs: [
    {
      q: "Which plans include discount codes in Checkout DM?",
      a: "Automatic and manual coupons, time-based offers and count-based offers are on Growth and Pro. Quantity-based offers such as Buy X Get Y% Off are on Pro only.",
    },
    {
      q: "Can I offer free shipping above a certain cart value?",
      a: "Yes, on Starter, Growth and Pro. The shipping fee is waived automatically once the cart crosses the value you set, and it applies to every qualifying order.",
    },
    {
      q: "Can a customer use two discounts on one order?",
      a: "No. One discount applies per order. Automatic offers win over manual codes, and if several automatic offers qualify the customer gets the one that saves the most.",
    },
    {
      q: "Can a customer use the same code again?",
      a: "No. Once a customer has redeemed a rule, it does not apply to their next order, so you do not need to expire codes to stop repeat use.",
    },
    {
      q: "Does a shipping fee apply on the Free plan?",
      a: "Yes. A flat shipping fee is available on every plan that sells physical products, and you can set it to 0 for free shipping.",
    },
  ],
};

export default post;
